export type ThemeMode = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

export const DEFAULT_THEME_MODE: ThemeMode = "system";
export const THEME_STORAGE_KEY = "runic-theme-mode";
export const DARK_THEME_CLASS = "dark";
export const THEME_DATA_ATTRIBUTE = "data-theme";

export const THEME_META_COLORS: Record<ResolvedTheme, string> = {
  light: "#f6f7f9",
  dark: "#091724",
};

export const isThemeMode = (value: unknown): value is ThemeMode =>
  value === "light" || value === "dark" || value === "system";

export const resolveTheme = (
  mode: ThemeMode,
  prefersDark: boolean
): ResolvedTheme => {
  if (mode === "system") {
    return prefersDark ? "dark" : "light";
  }
  return mode;
};

const getThemeColorMeta = (): HTMLMetaElement | null => {
  if (typeof document === "undefined") {
    return null;
  }
  return document.querySelector('meta[name="theme-color"]');
};

export const applyResolvedTheme = (resolvedTheme: ResolvedTheme) => {
  if (typeof document === "undefined") {
    return;
  }

  const root = document.documentElement;
  root.classList.toggle(DARK_THEME_CLASS, resolvedTheme === "dark");
  root.setAttribute(THEME_DATA_ATTRIBUTE, resolvedTheme);
  root.style.colorScheme = resolvedTheme;

  const themeColorMeta = getThemeColorMeta();
  if (themeColorMeta) {
    themeColorMeta.setAttribute("content", THEME_META_COLORS[resolvedTheme]);
  }
};

export const readStoredThemeMode = (): ThemeMode => {
  if (typeof window === "undefined") {
    return DEFAULT_THEME_MODE;
  }

  try {
    const storedValue = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isThemeMode(storedValue) ? storedValue : DEFAULT_THEME_MODE;
  } catch {
    return DEFAULT_THEME_MODE;
  }
};

export const writeStoredThemeMode = (mode: ThemeMode) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, mode);
  } catch {
    // Ignore storage failures and keep the in-memory preference.
  }
};

export const getSystemPrefersDark = (): boolean => {
  if (
    typeof window === "undefined" ||
    typeof window.matchMedia !== "function"
  ) {
    return false;
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
};

export const getThemeBootScript = () => {
  const storageKey = JSON.stringify(THEME_STORAGE_KEY);
  const defaultMode = JSON.stringify(DEFAULT_THEME_MODE);
  const darkClassName = JSON.stringify(DARK_THEME_CLASS);
  const dataAttribute = JSON.stringify(THEME_DATA_ATTRIBUTE);
  const lightColor = JSON.stringify(THEME_META_COLORS.light);
  const darkColor = JSON.stringify(THEME_META_COLORS.dark);

  return `
    (function () {
      var storageKey = ${storageKey};
      var defaultMode = ${defaultMode};
      var darkClassName = ${darkClassName};
      var dataAttribute = ${dataAttribute};
      var themeColors = { light: ${lightColor}, dark: ${darkColor} };

      function isThemeMode(value) {
        return value === "light" || value === "dark" || value === "system";
      }

      function resolveTheme(mode, prefersDark) {
        if (mode === "system") {
          return prefersDark ? "dark" : "light";
        }
        return mode;
      }

      function applyTheme(resolvedTheme) {
        var root = document.documentElement;
        root.classList.toggle(darkClassName, resolvedTheme === "dark");
        root.setAttribute(dataAttribute, resolvedTheme);
        root.style.colorScheme = resolvedTheme;

        var themeColorMeta = document.querySelector('meta[name="theme-color"]');
        if (themeColorMeta) {
          themeColorMeta.setAttribute("content", themeColors[resolvedTheme]);
        }
      }

      var storedMode = defaultMode;
      try {
        var nextMode = window.localStorage.getItem(storageKey);
        if (isThemeMode(nextMode)) {
          storedMode = nextMode;
        }
      } catch (error) {
        storedMode = defaultMode;
      }

      var prefersDark =
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-color-scheme: dark)").matches;
      applyTheme(resolveTheme(storedMode, prefersDark));
    })();
  `;
};
